/**
 * ============================================================================
 * [P1-02] Header Component
 *
 * 애플리케이션 헤더 컴포넌트
 * - 타이틀 및 서브타이틀
 * - 사용자 인증 섹션 (로그인/로그아웃)
 *
 * Props:
 * - user: 현재 로그인된 사용자 (null이면 로그인 폼 표시)
 * - onLoginWithGoogle: Google 로그인 핸들러
 * - onLogout: 로그아웃 핸들러
 * - onRefresh: 데이터 새로고침 핸들러
 * - isLoading: 로딩 상태
 * ============================================================================
 */

import type { User } from 'firebase/auth'
import styles from './Layout.module.css'

// ============================================================================
// 타입 정의
// ============================================================================
interface HeaderProps {
  user: User | null
  isLoading?: boolean
  onLoginWithGoogle: () => void
  onLogout: () => void
  onRefresh?: () => void
}

// ============================================================================
// [P1-02] Header 컴포넌트
// ============================================================================
export function Header({
  user,
  isLoading = false,
  onLoginWithGoogle,
  onLogout,
  onRefresh,
}: HeaderProps) {
  return (
    <header className={styles.header}>
      {/* ================================================================
          타이틀 영역
          ================================================================ */}
      <h1 className={styles.headerTitle}>
        <span className={styles.headerIcon}>📝</span>
        500자 미만 글 작성기
      </h1>
      <p className={styles.subtitle}>간단하고 깔끔한 글 작성 도구</p>

      {/* ================================================================
          인증 섹션
          ================================================================ */}
      <div className={styles.authSection}>
        {user ? (
          // 로그인 상태
          <UserInfo
            user={user}
            onLogout={onLogout}
            onRefresh={onRefresh}
          />
        ) : (
          // 로그아웃 상태
          <LoginForm
            onLoginWithGoogle={onLoginWithGoogle}
            isLoading={isLoading}
          />
        )}
      </div>
    </header>
  )
}

// ============================================================================
// LoginForm 서브 컴포넌트
// ============================================================================
interface LoginFormProps {
  onLoginWithGoogle: () => void
  isLoading: boolean
}

function LoginForm({ onLoginWithGoogle, isLoading }: LoginFormProps) {
  return (
    <div className={styles.loginForm}>
      <h3 className={styles.loginTitle}>🔐 안전한 로그인</h3>

      {/* Google 로그인 */}
      <div className={styles.googleLoginSection}>
        <button
          className={styles.googleBtn}
          onClick={onLoginWithGoogle}
          disabled={isLoading}
        >
          <span className={styles.googleIcon}>🔍</span>
          {isLoading ? '로그인 중...' : 'Google로 로그인'}
        </button>
        <p className={styles.googleNote}>
          Google 계정으로 안전하게 로그인하세요
        </p>
      </div>

      {/* 구분선 */}
      <div className={styles.authDivider}>
        <span>또는</span>
      </div>

      {/* 레거시 로그인 (추후 구현) */}
      <div className={styles.legacyLoginSection}>
        <p className={styles.authNote}>
          기존 사용자명 로그인은 React 버전에서 지원 예정
        </p>
      </div>
    </div>
  )
}

// ============================================================================
// UserInfo 서브 컴포넌트
// ============================================================================
interface UserInfoProps {
  user: User
  onLogout: () => void
  onRefresh?: () => void
}

function UserInfo({ user, onLogout, onRefresh }: UserInfoProps) {
  return (
    <div className={styles.userInfo}>
      {/* 사용자 프로필 이미지 */}
      {user.photoURL ? (
        <img
          src={user.photoURL}
          alt="프로필"
          className={styles.userPhoto}
        />
      ) : (
        <span className={styles.userIcon}>👤</span>
      )}

      {/* 사용자 이름 */}
      <span className={styles.usernameDisplay}>
        {user.displayName || user.email || '사용자'}
      </span>

      {/* 새로고침 버튼 */}
      {onRefresh && (
        <button
          className={`${styles.refreshBtn} ${styles.desktopOnly}`}
          onClick={onRefresh}
          title="데이터 새로고침"
          aria-label="데이터 새로고침"
        >
          <span>🔄</span>
          <span className={styles.desktopOnly}>새로고침</span>
        </button>
      )}

      {/* 로그아웃 버튼 */}
      <button
        className={styles.logoutBtn}
        onClick={onLogout}
      >
        로그아웃
      </button>
    </div>
  )
}

// ============================================================================
// 기본 export
// ============================================================================
export default Header
